import { useEffect, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PremiumBadge, LevelBadge, ReliabilityRow } from "@/components/Badges";
import DemoBadge from "@/components/DemoBadge";
import { MapPin, Heart, Ruler } from "lucide-react";
import { toast } from "sonner";
import { TALENT_TYPES as ALL_TALENT_TYPES, labelTalentType } from "@/lib/roleCategories";

const LEVELS = [
  { value: "all", label: "Tous les niveaux" },
  { value: "Débutant", label: "Débutant" },
  { value: "Talent actif", label: "Talent actif" },
  { value: "Talent confirmé", label: "Talent confirmé" },
  { value: "Premium", label: "Premium" },
];

const TALENT_TYPES = [
  { value: "all", label: "Tous" },
  ...ALL_TALENT_TYPES,
];

export default function TalentsList() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [talents, setTalents] = useState([]);
  const initial = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    location: initial.get("location") || "",
    talent_type: initial.get("talent_type") || "all",
    level: "all",
    premium_only: false,
    min_rating: "all",
    sort: "default",
  });

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && user.role !== "creator" && user.role !== "professional" && user.role !== "admin") navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, filters]);

  const load = async () => {
    const params = {};
    if (filters.location) params.location = filters.location;
    if (filters.talent_type && filters.talent_type !== "all") params.talent_type = filters.talent_type;
    if (filters.level && filters.level !== "all") params.level = filters.level;
    if (filters.premium_only) params.premium_only = true;
    if (filters.min_rating && filters.min_rating !== "all") params.min_rating = parseFloat(filters.min_rating);
    if (filters.sort && filters.sort !== "default") params.sort = filters.sort;
    try {
      const { data } = await api.get("/talents", { params });
      setTalents(Array.isArray(data) ? data : []);
    } catch {
      setTalents([]);
    }
  };

  if (!user) return null;

  return (
    <AppShell>
      <div data-testid="talents-list-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <p className="caption-label text-[#C68E59] mb-3">Découvrir</p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mb-10">Les talents Helencia</h1>

        {/* Filters */}
        <div className="card-lux p-6 mb-10 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
          <div>
            <Label className="caption-label">Type de talent</Label>
            <Select value={filters.talent_type} onValueChange={(v) => setFilters({ ...filters, talent_type: v })}>
              <SelectTrigger className="rounded-xl mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TALENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="caption-label">Localisation</Label>
            <Input value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} placeholder="Paris…" className="rounded-xl mt-2" />
          </div>
          <div>
            <Label className="caption-label">Niveau</Label>
            <Select value={filters.level} onValueChange={(v) => setFilters({ ...filters, level: v })}>
              <SelectTrigger className="rounded-xl mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEVELS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="caption-label">Note minimale</Label>
            <Select value={filters.min_rating} onValueChange={(v) => setFilters({ ...filters, min_rating: v })}>
              <SelectTrigger className="rounded-xl mt-2" data-testid="filter-min-rating"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="3">≥ 3 ★</SelectItem>
                <SelectItem value="4">≥ 4 ★</SelectItem>
                <SelectItem value="4.5">≥ 4.5 ★</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="caption-label">Tri</Label>
            <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
              <SelectTrigger className="rounded-xl mt-2" data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Premium d'abord</SelectItem>
                <SelectItem value="rating">Mieux notés</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-5">
            <Switch checked={filters.premium_only} onCheckedChange={(v) => setFilters({ ...filters, premium_only: v })} data-testid="filter-premium-only" />
            <Label className="caption-label m-0">Premium</Label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {talents.map((t) => (
            <div key={t.user_id} className="relative group flex" data-testid={`talent-card-${t.user_id}`}>
              <Link to={`/talents/${t.user_id}`} className="card-lux overflow-hidden flex flex-col w-full">
                <div className="aspect-[4/5] bg-[#FAF3EA] relative overflow-hidden shrink-0">
                  <TalentMainPhoto userId={t.user_id} demo={t.demo_photo_url} />
                  {t.is_premium && (
                    <div className="absolute top-3 right-3"><PremiumBadge /></div>
                  )}
                  {t.is_demo && (
                    <div className="absolute top-3 left-3"><DemoBadge /></div>
                  )}
                  {t.avg_rating > 0 && t.review_count > 0 && (
                    <div className="absolute bottom-3 right-3" data-testid={`talent-rating-${t.user_id}`}>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#1A1A1A]/85 backdrop-blur text-[#D4A574]">
                        ★ {t.avg_rating.toFixed(1)} <span className="text-white/40 font-normal">·{t.review_count}</span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif text-xl text-[#1A1A1A]">{t.name}</h3>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs text-[#666] mb-2 min-h-[16px]">
                    {t.talent_type && <span>{labelTalentType(t.talent_type)}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#666]">
                    <MapPin className="w-3.5 h-3.5" />{t.location || "—"}
                  </div>
                  {(t.height || t.measurements) && (
                    <div className="flex items-center gap-2 flex-wrap text-xs text-[#1A1A1A] mt-2" data-testid={`talent-measurements-${t.user_id}`}>
                      {t.height && <span className="px-2 py-0.5 rounded-full bg-[#FAF3EA] border border-[#EADDC9]"><Ruler className="inline w-3 h-3 mr-1 text-[#C68E59]" />{t.height}</span>}
                      {t.measurements && <span className="px-2 py-0.5 rounded-full bg-[#FAF3EA] border border-[#EADDC9]">{t.measurements}</span>}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <LevelBadge level={t.level} />
                  </div>
                  {t.reliability_badges?.length > 0 && (
                    <ReliabilityRow badges={t.reliability_badges} rating={0} count={0} max={3} className="mt-2" />
                  )}
                </div>
              </Link>
              {(user?.role === "creator" || user?.role === "professional") && (
                <FavoriteButton talentId={t.user_id} />
              )}
            </div>
          ))}
        </div>

        {talents.length === 0 && (
          <div className="text-center py-16 text-[#666]">Aucun talent ne correspond aux filtres.</div>
        )}
      </div>
      </div>
    </AppShell>
  );
}

function TalentMainPhoto({ userId, demo }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    api.get(`/portfolio/${userId}`)
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : [];
        const main = arr.find((p) => p.is_main) || arr[0];
        if (main) {
          // Demo items store the full external URL in storage_path.
          const sp = main.storage_path || "";
          setUrl(optimizeUrl(sp.startsWith("http") ? sp : `${API}/files/${sp}`, 640));
        } else if (demo) setUrl(demo);
      })
      .catch(() => { if (demo) setUrl(demo); });
  }, [userId, demo]);
  if (!url) return <div className="w-full h-full bg-[#FAF3EA]" />;
  return <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition duration-500" />;
}

function FavoriteButton({ talentId }) {
  const [isFav, setIsFav] = useState(false);
  useEffect(() => {
    api.get(`/favorites/check/${talentId}`)
      .then(({ data }) => setIsFav(!!data?.is_favorite))
      .catch(() => setIsFav(false));
  }, [talentId]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isFav) {
        await api.delete(`/favorites/${talentId}`);
        setIsFav(false);
        toast.success("Retiré des favoris");
      } else {
        await api.post(`/favorites/${talentId}`);
        setIsFav(true);
        toast.success("Ajouté aux favoris");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <button
      onClick={toggle}
      className="absolute top-3 left-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center hover:bg-white shadow transition z-10"
      data-testid={`fav-toggle-${talentId}`}
      title={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
    >
      <Heart className={`w-4 h-4 ${isFav ? "fill-[#D32F2F] text-[#D32F2F]" : "text-[#666]"}`} />
    </button>
  );
}
